/**
 * Tier naming and detection utilities for consistent tier handling
 */

export const VALID_TIERS = [
  'nonprofit-starter',
  'nonprofit-pro',
  'consultant-standard',
  'consultant-pro'
];

export const TIER_MAPPING = {
  'nonprofit': 'nonprofit-starter',         // Generic nonprofit → default tier
  'nonprofit-starter': 'nonprofit-starter',
  'nonprofit-pro': 'nonprofit-pro',
  'consultant': 'consultant-standard',      // Generic consultant → default tier
  'consultant-standard': 'consultant-standard',
  'consultant-pro': 'consultant-pro'
};

/**
 * Convert any tier input to canonical tier name
 * @param {string} tierInput - Tier name (can be generic or specific)
 * @returns {string} Canonical tier name
 * @example
 * normalizeTierName('consultant') // => 'consultant-standard'
 * normalizeTierName('nonprofit-pro') // => 'nonprofit-pro'
 */
export function normalizeTierName(tierInput) {
  if (!tierInput) return 'nonprofit-starter';
  return TIER_MAPPING[tierInput] || 'nonprofit-starter';
}

/**
 * Check if tier is consultant type
 * @param {string} tierName - Tier name to check
 * @returns {boolean} True if consultant tier
 * @example
 * isConsultantTier('consultant-standard') // => true
 * isConsultantTier('nonprofit-pro') // => false
 */
export function isConsultantTier(tierName) {
  if (!tierName) return false;
  return tierName.startsWith('consultant');
}

/**
 * Check if tier is nonprofit type
 * @param {string} tierName - Tier name to check
 * @returns {boolean} True if nonprofit tier
 * @example
 * isNonprofitTier('nonprofit-starter') // => true
 * isNonprofitTier('consultant-pro') // => false
 */
export function isNonprofitTier(tierName) {
  if (!tierName) return true; // Default to nonprofit
  return tierName.startsWith('nonprofit');
}

/**
 * Get managementType from tier name
 * @param {string} tierName - Tier name
 * @returns {string} "consultant-managed" or "direct"
 * @example
 * getManagementType('consultant-standard') // => 'consultant-managed'
 * getManagementType('nonprofit-pro') // => 'direct'
 */
export function getManagementType(tierName) {
  return isConsultantTier(tierName) ? 'consultant-managed' : 'direct';
}

/**
 * Get email template category from tier name
 * @param {string} tierName - Tier name
 * @returns {string} "consultant" or "nonprofit"
 * @example
 * getEmailCategory('consultant-pro') // => 'consultant'
 * getEmailCategory('nonprofit-starter') // => 'nonprofit'
 */
export function getEmailCategory(tierName) {
  return isConsultantTier(tierName) ? 'consultant' : 'nonprofit';
}

/**
 * Validate tier name is in approved list
 * @param {string} tierName - Tier name to validate
 * @returns {boolean} True if valid tier name
 * @example
 * validateTierName('consultant-standard') // => true
 * validateTierName('invalid-tier') // => false
 */
export function validateTierName(tierName) {
  const normalized = normalizeTierName(tierName);
  return VALID_TIERS.includes(normalized);
}

/**
 * Get tier family (consultant or nonprofit) from tier name
 * @param {string} tierName - Tier name
 * @returns {string} "consultant" or "nonprofit"
 * @example
 * getTierFamily('consultant-pro') // => 'consultant'
 * getTierFamily('nonprofit-starter') // => 'nonprofit'
 */
export function getTierFamily(tierName) {
  if (!tierName) return 'nonprofit';
  return tierName.startsWith('consultant') ? 'consultant' : 'nonprofit';
}

/**
 * Get tier level (starter, standard, pro) from tier name
 * @param {string} tierName - Tier name
 * @returns {string} "starter", "standard", "pro", or "unknown"
 * @example
 * getTierLevel('consultant-standard') // => 'standard'
 * getTierLevel('nonprofit-pro') // => 'pro'
 */
export function getTierLevel(tierName) {
  if (!tierName) return 'unknown';
  
  if (tierName.includes('starter')) return 'starter';
  if (tierName.includes('standard')) return 'standard';
  if (tierName.includes('pro')) return 'pro';
  
  return 'unknown';
}

/**
 * Get display-friendly tier name
 * @param {string} tierName - Tier name
 * @returns {string} Display name
 * @example
 * getTierDisplayName('consultant-standard') // => 'Consultant Standard'
 * getTierDisplayName('nonprofit-pro') // => 'Nonprofit Pro'
 */
export function getTierDisplayName(tierName) {
  if (!tierName) return 'Nonprofit Starter';
  
  const parts = tierName.split('-');
  return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}
